import type { SignatureBox } from 'services/entities/SignatureTypes';
import type { BlockMetadata, BlocksContentCollection, GridState } from '../grid/reduxStore/editorSlice';
import { GridBlockType } from 'services/entities/BlockTypes';
import { TableTypeIdentifier } from '../grid/reduxStore/table.types';
import { ALLOWED_DECIMAL_PLACES_FOR_DIMENSION } from '../hooks/UseBlockDimensionChangedHandler.ts';
import type { Section, SectionContentCollectionType } from '../Sections/Section.model';
import { gridPixelSize, gridSnapDistanceInPx } from '../shared/gridConfig';
import type { Margin } from '../SidePanel/DocumentSettings/DocumentDesignSettings/models/Margin.model';
import { getSignaturesMaxHeight } from '../SidePanel/Signatures/SignatureHelper';
import { DEFAULT_DECIMAL_PLACES } from './Block/Table/defaultData';
import type { BlockConfig } from './models/BlockConfig.model';
import type { TrackedBlockCollection } from './models/TrackedBlockCollection.model';

export const maxImageWidthAllowedInPx = 500;
export const maxImageHeightAllowedInPx = 500;

export function collectSignaturesFromSections(sections: Section[]): Record<string, SignatureBox[]> | undefined {
  if (sections.length === 0) return undefined;

  const result: Record<string, SignatureBox[]> = {};
  sections.forEach((section) => {
    const signatures = Object.values(section.blocksContent)
      .filter((b) => b.type === GridBlockType.SIGNATURE && b.contentSignature)
      .map((b) => b.contentSignature as SignatureBox);
    if (signatures.length > 0) {
      result[section.id] = signatures;
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

export function convertSectionsLoadDataToContentBlocks(
  sectionsData: SectionContentCollectionType,
  signaturesToMerge?: Record<string, SignatureBox[]>
): GridState {
  const defaultSection = {
    sections: [] as Section[],
    blocksContent: {},
    blocksMetadata: [],
    blocksLayer: { greaterZIndexAvailable: 0, lowerZIndexAvailable: 0 },
    editorConfig: { currentManipulatedBlock: null, maxHeightPage: 0 },
    temporaryDimensions: {},
    ...(sectionsData.sections[0]?.styles ? { styles: sectionsData.sections[0].styles } : {}),
  };

  return sectionsData.sections.reduce((reducedData, section) => {
    const sectionId = section.id;
    const sectionBlocksContent: Record<string, any> = {};
    const sectionBlocksMetadata: BlockMetadata[] = [];
    const sectionBlocksLayer = { greaterZIndexAvailable: 0, lowerZIndexAvailable: 0 };

    const addBlockToSection = (blockConfig: BlockConfig, type: GridBlockType, blockId: string, content: any = {}) => {
      sectionBlocksContent[blockId] = {
        ...content,
        blockConfig,
        type,
      };
      sectionBlocksMetadata.push({ id: blockId, type, imageAspectRatioLock: true });
      sectionBlocksLayer.greaterZIndexAvailable = Math.max(blockConfig.z + 1, sectionBlocksLayer.greaterZIndexAvailable);
      sectionBlocksLayer.lowerZIndexAvailable = Math.min(blockConfig.z - 1, sectionBlocksLayer.lowerZIndexAvailable);
    };

    section.blocks.forEach((block) => {
      if (block.tableContent === undefined) {
        block.tableContent = {
          rows: [],
          columns: [],
          totalRows: [],
          metadata: {
            tableType: TableTypeIdentifier.TEXT_TABLE,
            pricingDecimalPlaces: DEFAULT_DECIMAL_PLACES,
          },
          selectedRow: null,
        };
      }

      if (block.blockId) {
        const blockConfig = {
          id: block.blockId,
          height: block.dimensions.height_px,
          width: block.dimensions.width_px,
          y: block.position.top_px,
          x: block.position.left_px,
          z: block.zIndex,
        };

        const content = {
          content: block.htmlContent,
          contentObject: block.content,
          contentTable: block.tableContent,
        };

        addBlockToSection(blockConfig, block.type, block.blockId, content);
      }
    });

    if (signaturesToMerge && signaturesToMerge[sectionId]) {
      signaturesToMerge[sectionId].forEach((signature) => {
        const blockConfig = {
          id: signature.signatureBoxId,
          x: signature.properties.position.x,
          y: signature.properties.position.y,
          width: signature.properties.dimensions.width,
          height: signature.properties.dimensions.height,
          z: sectionBlocksLayer.greaterZIndexAvailable,
        };

        const content = {
          content: '',
          contentSignature: signature,
        };

        addBlockToSection(blockConfig, GridBlockType.SIGNATURE, signature.signatureBoxId, content);
      });
    }

    reducedData.sections.push({
      id: sectionId,
      title: section.title,
      blocksContent: sectionBlocksContent,
      blocksMetadata: sectionBlocksMetadata,
      blocksLayer: sectionBlocksLayer,
      editorConfig: { currentManipulatedBlock: null, maxHeightPage: 0 },
      ...(section.styles ? { styles: section.styles } : {}),
    });

    return reducedData;
  }, defaultSection);
}

export function getEditorMaxHeight(blocksContent: BlocksContentCollection | object, ...otherMaxValues: number[]) {
  let gridBlockMaxHeight = 0;
  if (blocksContent) {
    gridBlockMaxHeight = Object.entries(blocksContent).reduce((currentMax, blockContent) => {
      return Math.max(currentMax, blockContent[1].blockConfig.y + (blockContent[1].blockConfig.height || 0));
    }, 0);
  }

  return Math.max(gridBlockMaxHeight, ...otherMaxValues) + gridPixelSize * gridPixelSize;
}

export function roundToNearestMultipleOfGridSize(value: number) {
  return Math.round(value / gridPixelSize) * gridPixelSize;
}

export const getRoundedValue = (value: number, decimalPlaces: number): number => {
  return Number(value.toFixed(decimalPlaces));
};

export const calculateImageWidthAndHeight = (actualWidth: number, actualHeight: number) => {
  if (actualWidth <= maxImageWidthAllowedInPx && actualHeight <= maxImageHeightAllowedInPx) {
    return { calculatedWidth: actualWidth, calculatedHeight: actualHeight };
  }

  let calculatedWidth = actualWidth;
  let calculatedHeight = actualHeight;

  if (actualWidth > maxImageWidthAllowedInPx) {
    const aspectRatio = actualHeight / actualWidth;
    calculatedWidth = maxImageWidthAllowedInPx;
    calculatedHeight = calculatedWidth * aspectRatio;
  }

  if (calculatedHeight > maxImageHeightAllowedInPx) {
    const newAspectRatioAfterSettingHeight = calculatedWidth / calculatedHeight;
    calculatedHeight = maxImageHeightAllowedInPx;
    calculatedWidth = calculatedHeight * newAspectRatioAfterSettingHeight;
  }

  return {
    calculatedWidth: getRoundedValue(calculatedWidth, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
    calculatedHeight: getRoundedValue(calculatedHeight, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
  };
};

export const calculateReplacementImageDimensions = (imageWidth: number, imageHeight: number, targetHeight: number) => {
  const calculatedWidth =
    imageHeight > 0 ? getRoundedValue((imageWidth / imageHeight) * targetHeight, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION) : targetHeight;
  return { calculatedWidth, calculatedHeight: targetHeight };
};

const isElementVisibleFactory = (editorHeaderHeight: number) => {
  return (blockElement: Element | null) => {
    if (!blockElement) return false;
    const { top, bottom } = blockElement.getBoundingClientRect();

    return (top >= editorHeaderHeight && top <= window.innerHeight) || (bottom <= window.innerHeight && bottom >= editorHeaderHeight);
  };
};

export function setupTrackedBlocks(gridBlocks: BlocksContentCollection, signatures: SignatureBox[]): TrackedBlockCollection {
  const collection = {};
  const editorHeaderHeight = document.querySelector('.editor-header-wrapper')?.getBoundingClientRect().height || 0;
  const isElementVisible = isElementVisibleFactory(editorHeaderHeight);
  Object.values(gridBlocks).forEach((gridBlock) => {
    const blockElement = document.querySelector(`.block_${gridBlock.blockConfig.id}`);
    if (!isElementVisible(blockElement)) {
      return;
    }

    collection[gridBlock.blockConfig.id] = {
      height: gridBlock.blockConfig.height,
      width: gridBlock.blockConfig.width,
      x: gridBlock.blockConfig.x,
      y: gridBlock.blockConfig.y,
    };
  });

  signatures.forEach((signature) => {
    const blockElement = document.querySelector(`.signature_${signature.signatureBoxId}`);
    if (!isElementVisible(blockElement)) {
      return;
    }

    collection[signature.signatureBoxId] = {
      height: signature.properties.dimensions.height,
      width: signature.properties.dimensions.width,
      x: signature.properties.position.x,
      y: signature.properties.position.y,
    };
  });

  return collection;
}

export function calculatePageMaxHeight(gridBlocks: BlocksContentCollection, signatureBlocks?: SignatureBox[], activeBlock?: BlockConfig) {
  const pageMaxHeightBasedOnGridBlocks = getPageMaxHeightBasedOnGridBlocks(gridBlocks, activeBlock);
  const pageMaxHeightBasedOnSignatureBlocks = getSignaturesMaxHeight(signatureBlocks);
  // whatever is greater, return that — min-height is enforced by CSS via --canvas-min-height
  return Math.max(pageMaxHeightBasedOnSignatureBlocks, pageMaxHeightBasedOnGridBlocks);
}

export function getPageMaxHeightBasedOnGridBlocks(gridBlocks: BlocksContentCollection, activeBlock?: BlockConfig) {
  let pageMaxHeight = 0;
  for (const key in gridBlocks) {
    let currentBlockPageHeight: number;

    /* gridBlocks parameter doesn't contain an updated state of a block that is currently being dragged,
    resized or having its content changed. So we rely on activeBlock being passed with the updated state. */
    if (activeBlock && key === activeBlock.id) {
      currentBlockPageHeight = activeBlock.height + activeBlock.y;
    } else {
      currentBlockPageHeight = gridBlocks[key].blockConfig.y + (gridBlocks[key].blockConfig.height || 0);
    }

    if (pageMaxHeight < currentBlockPageHeight) {
      pageMaxHeight = currentBlockPageHeight;
    }
  }
  return pageMaxHeight;
}

export function getBoundingPosition(currentBlock: BlockConfig, boundaries?: { height: number; width: number }) {
  if (!boundaries) return { x: null, y: null };
  const { width: blockWidth, x: blockX, y: blockY } = currentBlock;

  const { width: boundaryWidth } = boundaries;

  const blockRight = blockX + blockWidth;

  let reachedBoundaryX: number | null = null;
  let reachedBoundaryY: number | null = null;

  if (blockX < 0) {
    reachedBoundaryX = 0;
  } else if (blockRight > boundaryWidth) {
    reachedBoundaryX = boundaryWidth - blockWidth;
  }
  // Prevent the block from moving to the top of the page, but allow it to move over the bottom of the block.
  if (blockY < 0) reachedBoundaryY = 0;

  return { x: reachedBoundaryX, y: reachedBoundaryY };
}

function findSnapMatchesPosition(
  currentBlockPoints: number[],
  id: string,
  currentBlock: BlockConfig,
  trackedBlockPoints: number[],
  pointAdjustment: number[],
  fixedTrackedPoints: number[]
): number[] {
  const snapMatches: number[] = [];

  for (let i = 0; i < currentBlockPoints.length; i++) {
    // Check against moving blocks positions
    if (id !== currentBlock.id) {
      for (let j = 0; j < trackedBlockPoints.length; j++) {
        const distanceBetweenBlocks = currentBlockPoints[i] - trackedBlockPoints[j];

        if (distanceBetweenBlocks >= -gridSnapDistanceInPx && distanceBetweenBlocks <= gridSnapDistanceInPx) {
          snapMatches.push(trackedBlockPoints[j] - pointAdjustment[i]);
        }
      }
    }

    // Check against fixed positions
    for (let j = 0; j < fixedTrackedPoints.length; j++) {
      const distanceBetweenBlocks = currentBlockPoints[i] - fixedTrackedPoints[j];

      if (distanceBetweenBlocks >= -gridSnapDistanceInPx && distanceBetweenBlocks <= gridSnapDistanceInPx) {
        snapMatches.push(fixedTrackedPoints[j] - pointAdjustment[i]);
      }
    }
  }
  return snapMatches;
}

export function getSnapPosition(
  currentBlock: BlockConfig,
  trackedBlockCollection: TrackedBlockCollection,
  pageMaxWidthPx: number,
  margins: Margin
) {
  if (!currentBlock || !Object.keys(trackedBlockCollection).length) {
    return { x: null, y: null };
  }

  const possibleSnappingPositions: { xAxisMatchesPx: number[]; yAxisMatchesPx: number[] } = {
    xAxisMatchesPx: [],
    yAxisMatchesPx: [],
  };

  const currentBlockHeight = currentBlock.height;
  const currentBlockWidth = currentBlock.width;

  const draggedBlockBottom = currentBlock.y + currentBlockHeight;
  const draggedBlockRight = currentBlock.x + currentBlockWidth;

  const draggedBlockMiddleX = currentBlock.x + currentBlockWidth / 2;
  const draggedBlockMiddleY = currentBlock.y + currentBlockHeight / 2;

  const pageMiddleX = pageMaxWidthPx / 2;

  const currentBlockHalfWidth = currentBlock.width / 2;
  const currentBlockHalfHeight = currentBlock.height / 2;

  const marginRightXPx = pageMaxWidthPx - margins.right;

  const currentBlockXPoints: number[] = [currentBlock.x, draggedBlockRight, draggedBlockMiddleX];

  const currentBlockYPoints: number[] = [currentBlock.y, draggedBlockBottom, draggedBlockMiddleY];

  // We use these 2 arrays to adjust the resulting x and y values based on the currentBlock's
  // width and height during a snap action.
  const xAdjustment: number[] = [0, currentBlock.width, currentBlockHalfWidth];
  const yAdjustment: number[] = [0, currentBlock.height, currentBlockHalfHeight];

  const fixedTrackedXPoints = [pageMiddleX, margins.left, marginRightXPx];

  const fixedTrackedYPoints = [margins.top];

  for (const [id, trackedData] of Object.entries(trackedBlockCollection)) {
    const trackedBlockHeight = trackedData.height;
    const trackedBlockWidth = trackedData.width;

    const trackedBlockBottom = trackedData.y + trackedBlockHeight;
    const trackedBlockRight = trackedData.x + trackedBlockWidth;
    const trackedBlockMiddleX = trackedData.x + trackedBlockWidth / 2;
    const trackedBlockMiddleY = trackedData.y + trackedBlockHeight / 2;

    // The following arrays contain the dragged block and tracked block's X and Y points
    // for the 4 sides of each block and their middle points, as well as page middle points.
    const trackedBlockXPoints: number[] = [
      trackedData.x,
      trackedBlockRight,
      trackedBlockMiddleX,
      pageMiddleX,
      margins.left,
      marginRightXPx,
    ];

    const trackedBlockYPoints: number[] = [trackedData.y, trackedBlockBottom, trackedBlockMiddleY, margins.top];

    possibleSnappingPositions.xAxisMatchesPx = possibleSnappingPositions.xAxisMatchesPx.concat(
      findSnapMatchesPosition(currentBlockXPoints, id, currentBlock, trackedBlockXPoints, xAdjustment, fixedTrackedXPoints)
    );

    possibleSnappingPositions.yAxisMatchesPx = possibleSnappingPositions.yAxisMatchesPx.concat(
      findSnapMatchesPosition(currentBlockYPoints, id, currentBlock, trackedBlockYPoints, yAdjustment, fixedTrackedYPoints)
    );
  }

  // snap priority given to leftmost x and topmost y from each group
  const snapMatchX = possibleSnappingPositions.xAxisMatchesPx.length ? Math.min(...possibleSnappingPositions.xAxisMatchesPx) : null;
  const snapMatchY = possibleSnappingPositions.yAxisMatchesPx.length ? Math.min(...possibleSnappingPositions.yAxisMatchesPx) : null;

  return {
    x: snapMatchX,
    y: snapMatchY,
  };
}
